// fr-CA writes 1234,56; every other supported locale writes 1234.56.
const getDecimalSeparator = (locale: string): string =>
  locale === "fr-CA" ? "," : ".";

export const formatCurrency = (
  amount: number | null,
  locale = "en-US"
): string => {
  if (amount === null) return "--";
  const formattedNumber = amount.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // For French Canadian locale, place $ after the number with a space
  if (locale === "fr-CA") {
    return `${formattedNumber} $`;
  }

  // For English locales, place $ before the number
  return `$${formattedNumber}`;
};

export const parseCurrency = (
  value: string,
  locale = "en-US"
): number | null => {
  // Stripping the fr-CA comma instead of reading it would make 1234,56 into 123456.
  const cleaned =
    getDecimalSeparator(locale) === ","
      ? value.replace(/[^0-9,]/g, "").replace(/,/g, ".")
      : value.replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return null;
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) return null;
  // Round to 2 decimal places
  return Math.round(parsed * 100) / 100;
};

export const validateFeeName = (name: string): boolean => {
  return name.trim().length > 0;
};

// en-US "," groups thousands; reading it as a decimal saved "1,200" as 1.20.
const stripGroupingSeparators = (rawValue: string, separator: string) => {
  if (separator === ".") return rawValue.replace(/,/g, "");
  // fr-CA: "." is a typed decimal unless a "," already marks one.
  return rawValue.includes(",")
    ? rawValue.replace(/\./g, "")
    : rawValue.replace(/\./g, ",");
};

// Normalises raw amount input to the locale separator, one separator, cents only.
export const sanitizeAmountInput = (
  rawValue: string,
  locale = "en-US"
): string => {
  const separator = getDecimalSeparator(locale);
  const normalized = stripGroupingSeparators(rawValue, separator);
  const filtered = normalized.replace(
    separator === "," ? /[^0-9,]/g : /[^0-9.]/g,
    ""
  );
  const [whole, ...rest] = filtered.split(separator);
  if (rest.length === 0) return filtered;
  // A paste never fires keydown, so this is the only guard that sees one.
  return whole + separator + rest.join("").slice(0, 2);
};

// Blocks keystrokes that cannot appear in an amount: non-numeric keys, a second
// decimal separator, and a third decimal digit.
export const blockNonAmountKey = (
  e: React.KeyboardEvent<HTMLInputElement>,
  currentAmount: string,
  locale = "en-US"
): void => {
  // Let the browser keep paste/select-all/copy/cut/undo.
  if (e.ctrlKey || e.metaKey) return;

  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Enter",
    "Home",
    "End",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown"
  ];
  const isNumeric = /^[0-9]$/.test(e.key);
  const isDecimal = e.key === "." || e.key === ",";

  if (!allowedKeys.includes(e.key) && !isNumeric && !isDecimal) {
    e.preventDefault();
    return;
  }

  // The guards below only apply to a keystroke that adds a character.
  const input: HTMLInputElement | null = e.currentTarget ?? null;
  const caret = input?.selectionStart ?? currentAmount.length;
  const isReplacingSelection = (input?.selectionEnd ?? caret) !== caret;
  if (isReplacingSelection) return;

  const separator = getDecimalSeparator(locale);
  const separatorIndex = currentAmount.indexOf(separator);

  if (isDecimal && separatorIndex !== -1) {
    e.preventDefault();
    return;
  }

  // Caret-aware: a whole-string check would block every digit once cents exist.
  if (isNumeric && separatorIndex !== -1 && caret > separatorIndex) {
    const fraction = currentAmount.slice(separatorIndex + 1);
    if (fraction.length >= 2) {
      e.preventDefault();
    }
  }
};

// Seeds an amount input from the stored value. Deliberately not padded to two
// decimals: blockNonAmountKey refuses a third decimal digit, so "299.00" would
// be un-appendable and read as a broken field.
export const formatAmountForInput = (
  amount: number | null,
  locale = "en-US"
): string =>
  amount === null
    ? ""
    : String(amount).replace(".", getDecimalSeparator(locale));
